import { useState } from "react";
import { useUnifiedWallet } from "~/hooks/useUnifiedWallet";
import { useWalletBalances } from "~/hooks/useWalletBalances";
import { useChains } from "~/hooks/useChains";
import { truncateAddress, formatAmount, formatUsd } from "~/lib/format";
import type { WalletBalance } from "~/api/types";

export function WalletDrawer({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { address, disconnect } = useUnifiedWallet();
  const { balances, totalValueUsd, isLoading, refetch } = useWalletBalances();
  const { chains } = useChains();
  const [activeTab, setActiveTab] = useState<"tokens" | "activity">("tokens");

  if (!open) return null;

  function handleSwapPrefill(balance: WalletBalance, side: "from" | "to") {
    const chain = chains.find((c) => c.chainId === balance.chainId);
    if (!chain) return;
    const token = {
      chainId: balance.chainId,
      address: balance.address,
      symbol: balance.symbol,
      name: balance.name,
      decimals: balance.decimals,
      logoUri: balance.logoUri,
    };
    window.dispatchEvent(
      new CustomEvent("swap-prefill", { detail: { side, chain, token } }),
    );
    onClose();
  }

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 z-40 bg-black/50"
        onClick={onClose}
        style={{ animation: "drawer-overlay-in 0.2s ease-out" }}
      />

      {/* Drawer */}
      <div
        className="fixed right-0 top-0 bottom-0 z-50 flex w-[min(400px,90vw)] flex-col border-l border-[var(--color-border-modal)] bg-[var(--color-bg-modal)]"
        style={{ animation: "drawer-slide-in 0.25s ease-out" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-[var(--color-border-modal)] px-5 py-4">
          <span className="font-mono text-sm text-[var(--color-text-primary)]">
            {address ? truncateAddress(address) : ""}
          </span>
          <button
            onClick={onClose}
            className="rounded-[var(--radius-sm)] p-1 text-[var(--color-text-secondary)] transition-colors hover:text-[var(--color-text-primary)]"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M12 4L4 12M4 4l8 8"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {/* Total value */}
        <div className="flex items-center justify-between px-5 py-4">
          <p className="text-2xl font-semibold text-[var(--color-text-primary)]">
            {isLoading ? "..." : formatUsd(String(totalValueUsd))}
          </p>
          <button
            onClick={() => refetch()}
            className="rounded-full p-2 text-[var(--color-text-secondary)] transition-colors hover:bg-[rgba(255,255,255,0.05)] hover:text-[var(--color-text-primary)]"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M14 8A6 6 0 1 1 8 2a5.94 5.94 0 0 1 4.24 1.76L14 2v6h-6l2.12-2.12A4 4 0 1 0 12 8h2Z"
                fill="currentColor"
              />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-[var(--color-border-modal)] px-5">
          {(["tokens", "activity"] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`border-b-2 px-4 pb-2 text-sm font-medium capitalize transition-colors ${
                activeTab === tab
                  ? "border-[var(--color-accent)] text-[var(--color-text-primary)]"
                  : "border-transparent text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {activeTab === "tokens" ? (
            isLoading ? (
              <div className="flex flex-col gap-2 p-4">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div
                    key={i}
                    className="h-[56px] animate-pulse rounded-[var(--radius-sm)] bg-[var(--color-bg-modal-input)]"
                  />
                ))}
              </div>
            ) : balances.length === 0 ? (
              <p className="py-16 text-center text-sm text-[var(--color-text-secondary)]">
                No tokens found
              </p>
            ) : (
              <div className="flex flex-col gap-0.5 p-3">
                {balances.map((b) => (
                  <BalanceRow
                    key={`${b.chainId}:${b.address}`}
                    balance={b}
                    onSell={() => handleSwapPrefill(b, "from")}
                    onBuy={() => handleSwapPrefill(b, "to")}
                  />
                ))}
              </div>
            )
          ) : (
            <p className="py-16 text-center text-sm text-[var(--color-text-secondary)]">
              Coming soon
            </p>
          )}
        </div>

        {/* Disconnect */}
        <div className="border-t border-[var(--color-border-modal)] px-5 py-3">
          <button
            onClick={() => {
              disconnect();
              onClose();
            }}
            className="w-full rounded-[var(--radius-md)] border border-[var(--color-danger)] px-4 py-2 text-sm font-medium text-[var(--color-danger)] transition-colors hover:bg-[rgba(239,68,68,0.1)]"
          >
            Disconnect
          </button>
        </div>
      </div>
    </>
  );
}

// ─── Balance Row ───

function BalanceRow({
  balance,
  onSell,
  onBuy,
}: {
  balance: WalletBalance;
  onSell: () => void;
  onBuy: () => void;
}) {
  return (
    <div className="group relative flex items-center gap-3 rounded-[var(--radius-sm)] px-3 py-2.5 transition-colors hover:bg-[rgba(246,246,246,0.05)]">
      {/* Token icon + chain badge */}
      <div className="relative h-9 w-9 shrink-0">
        {balance.logoUri ? (
          <img
            src={balance.logoUri}
            alt={balance.symbol}
            className="h-9 w-9 rounded-full"
          />
        ) : (
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[var(--color-bg-modal-input)] text-xs font-bold text-[#9b9b9b]">
            {balance.symbol.slice(0, 2)}
          </div>
        )}
        {balance.chainLogoUri && (
          <div className="absolute -right-0.5 -bottom-0.5 h-4 w-4 overflow-hidden rounded-[4px] border border-[var(--color-bg-modal)] bg-[var(--color-bg-modal)]">
            <img
              src={balance.chainLogoUri}
              alt={balance.chain}
              className="h-full w-full"
            />
          </div>
        )}
      </div>

      {/* Info */}
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-[var(--color-text-primary)]">
          {balance.symbol}
        </p>
        <p className="text-xs text-[#9b9b9b]">{balance.chain}</p>
      </div>

      {/* Value (hidden on group hover) */}
      <div className="text-right transition-opacity group-hover:opacity-0">
        <p className="text-sm font-medium text-[var(--color-text-primary)]">
          ${balance.valueUsd.toFixed(2)}
        </p>
        <p className="text-xs text-[#9b9b9b]">
          {formatAmount(balance.amount, balance.decimals)} {balance.symbol}
        </p>
      </div>

      {/* Buy / Sell (visible on hover) */}
      <div className="absolute right-3 top-1/2 hidden -translate-y-1/2 gap-1.5 group-hover:flex">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onBuy();
          }}
          className="rounded-md bg-[rgba(5,150,105,0.2)] px-3 py-1 text-xs font-medium text-[var(--color-success-step)] transition-colors hover:bg-[rgba(5,150,105,0.3)]"
        >
          Buy
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onSell();
          }}
          className="rounded-md bg-[rgba(239,68,68,0.2)] px-3 py-1 text-xs font-medium text-[var(--color-danger)] transition-colors hover:bg-[rgba(239,68,68,0.3)]"
        >
          Sell
        </button>
      </div>
    </div>
  );
}
